import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Autocomplete from '../Autocomplete';
import { methods as methodsApi } from '@afosto/fulfilment/api/Methods';
import { profile as profileApi } from '@afosto/iam/api/Profile';
import sortArrayByProperty from '@afosto/utils/array/sortArrayByProperty';
import { useFormContext } from '../Form';

const SHIPPING_METHODS_PATH = 'afosto.tenant.lcs.handling.v1.methods';

const formatValue = (suggestions, value) => {
  if (typeof value === 'object' && Array.isArray(value)) {
    return value
      .map(item => suggestions.find(suggestion => suggestion.value === item))
      .filter(item => item);
  }

  return suggestions.find(suggestion => suggestion.value === value);
};

const ShippingMethodSearch = ({
  clientId,
  countryCode,
  value: providedValue,
  name,
  valueOnly,
  multiple,
  onChange,
  ...props
}) => {
  const [shippingMethodSuggestions, setShippingMethodSuggestions] = useState([]);
  const [isFetchingShippingMethods, setIsFetchingShippingMethods] = useState(true);
  const formContext = useFormContext();
  const { setValue, watch } = formContext ?? {};
  const currentValue = providedValue ?? (watch && watch(name));
  const fallbackValue = multiple ? [] : null;
  const value = valueOnly ? formatValue(shippingMethodSuggestions, currentValue) : currentValue;

  useEffect(() => {
    let isMounted = true;

    const fetchMethods = async () => {
      try {
        setIsFetchingShippingMethods(true);

        const response = await methodsApi.listMethods(
          {
            countryCode,
          },
          {
            headers: {
              'x-page-size': 100,
            },
          },
        );
        const { data = [] } = response;
        let methods = data;

        if (clientId) {
          const profileResponse = await profileApi.getProfile(
            SHIPPING_METHODS_PATH.replace('tenant', clientId),
          );
          const { data: profileMethods = {} } = profileResponse ?? {};
          const shippingMethodCodes = (profileMethods?.methods ?? [])
            .filter(method => method.is_enabled)
            .map(method => method.code);

          methods = methods.filter(method => shippingMethodCodes.includes(method.id));
        }

        if (isMounted) {
          setShippingMethodSuggestions(
            sortArrayByProperty(
              methods.map(method => ({ ...method, value: method.id, label: method.name })),
              'provider',
            ),
          );
        }
      } catch (err) {
        if (isMounted) {
          setShippingMethodSuggestions([]);
        }
      } finally {
        if (isMounted) {
          setIsFetchingShippingMethods(false);
        }
      }
    };

    fetchMethods();

    return () => {
      isMounted = false;
    };
  }, [clientId, countryCode]);

  const handleSetValue = (ev, newValue) => {
    let updatedValue = newValue;

    if (typeof updatedValue === 'object' && valueOnly) {
      if (Array.isArray(updatedValue)) {
        updatedValue = updatedValue.map(item => item.value);
      } else {
        updatedValue = updatedValue.value;
      }
    }

    if (formContext && !onChange) {
      setValue(name, updatedValue);
      return;
    }

    if (onChange) {
      onChange(ev, updatedValue);
    }
  };

  /**
   * Handle the change of the Autocomplete
   * @param {object|Event} ev
   * @param {any} newValue
   */
  const handleOnChange = async (ev, newValue) => {
    try {
      if (newValue) {
        handleSetValue(ev, newValue);
      } else {
        handleSetValue(ev, null);
      }
    } catch (err) {
      handleSetValue(ev, null);
    }
  };

  return (
    <Autocomplete
      {...props}
      options={shippingMethodSuggestions}
      loading={isFetchingShippingMethods}
      onChange={handleOnChange}
      autocomplete="off"
      value={value ?? fallbackValue}
      multiple={multiple}
      name={name}
      groupBy={option => option.provider}
    />
  );
};

ShippingMethodSearch.propTypes = {
  clientId: PropTypes.string,
  countryCode: PropTypes.string,
  ...Autocomplete.propTypes,
};

ShippingMethodSearch.defaultProps = {
  clientId: undefined,
  countryCode: null,
  ...Autocomplete.defaultProps,
};

export default ShippingMethodSearch;
